import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, CalendarClock, Save } from "lucide-react";
import { toast } from "sonner";

const DAYS = [
  { v: 1, l: "Lundi" }, { v: 2, l: "Mardi" }, { v: 3, l: "Mercredi" },
  { v: 4, l: "Jeudi" }, { v: 5, l: "Vendredi" }, { v: 6, l: "Samedi" }, { v: 0, l: "Dimanche" },
];
const INTERVALS = [15, 30, 45, 60];

export default function CollectionSlots() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [days, setDays] = useState([]);
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("18:00");
  const [interval, setIntervalMin] = useState(30);
  const [preview, setPreview] = useState([]);

  const load = () => {
    setLoading(true);
    api.get("/settings").then((r) => {
      const s = r.data;
      setDays(s.collect_days || [1, 2, 3, 4, 5, 6]);
      setStart(s.collect_time_start || "08:00");
      setEnd(s.collect_time_end || "18:00");
      setIntervalMin(s.collect_slot_minutes || 30);
    }).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const toMin = (t) => { const [h, m] = t.split(":"); return +h * 60 + +m; };
    let a = toMin(start); const b = toMin(end); const out = [];
    const step = Math.max(interval, 5);
    while (a <= b && out.length < 60) { out.push(`${String(Math.floor(a / 60)).padStart(2, "0")}:${String(a % 60).padStart(2, "0")}`); a += step; }
    setPreview(out);
  }, [start, end, interval]);

  const toggleDay = (v) => setDays((d) => (d.includes(v) ? d.filter((x) => x !== v) : [...d, v]));

  const save = async () => {
    if (days.length === 0) { toast.error("Sélectionnez au moins un jour"); return; }
    setSaving(true);
    try {
      await api.put("/settings/collection", {
        collect_days: days, collect_time_start: start, collect_time_end: end, collect_slot_minutes: Number(interval),
      });
      toast.success("Créneaux de collecte enregistrés");
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="h-40 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-brand-600" /></div>;

  return (
    <div className="space-y-6 max-w-2xl">
      <div><h1 className="text-2xl sm:text-3xl font-bold font-heading">Créneaux de collecte</h1><p className="text-slate-500 text-sm mt-1">Définissez les jours et heures disponibles pour la collecte des clients</p></div>

      <div className="bg-white border rounded-2xl p-6 space-y-6">
        <div>
          <Label className="mb-2 block flex items-center gap-2"><CalendarClock className="w-4 h-4 text-brand-600" /> Jours de collecte disponibles</Label>
          <div className="flex flex-wrap gap-2">
            {DAYS.map((d) => (
              <button key={d.v} data-testid={`slot-day-${d.v}`} onClick={() => toggleDay(d.v)} className={`px-3 py-2 rounded-xl text-sm font-medium border transition-colors ${days.includes(d.v) ? "bg-brand-600 text-white border-brand-600" : "bg-white border-slate-200 text-slate-600"}`}>{d.l}</button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-3 gap-4">
          <div><Label>Heure de début</Label><Input data-testid="slot-start" type="time" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1" /></div>
          <div><Label>Heure de fin</Label><Input data-testid="slot-end" type="time" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1" /></div>
          <div>
            <Label>Intervalle (min)</Label>
            <Select value={String(interval)} onValueChange={(v) => setIntervalMin(Number(v))}>
              <SelectTrigger data-testid="slot-interval" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{INTERVALS.map((i) => <SelectItem key={i} value={String(i)}>{i} min</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label className="mb-2 block">Aperçu des créneaux ({preview.length})</Label>
          <div className="flex flex-wrap gap-1.5">
            {preview.map((t) => <span key={t} className="text-xs bg-brand-50 text-brand-700 border border-brand-200 rounded-lg px-2 py-1">{t}</span>)}
          </div>
        </div>

        <Button data-testid="slot-save" onClick={save} disabled={saving} className="bg-brand-600 hover:bg-brand-700">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4 mr-2" /> Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}
